import Head from 'next/head';
import { useRouter } from 'next/router';

/**
 * Public canonical host. All Restworld sub-apps (worker, employer,
 * ambassador) declare their canonical to the marketing domain so Google
 * consolidates indexing on the public www.restworld.it surface — the
 * subdomains themselves should not appear in search results.
 *
 * If this ever changes, update this single constant; all sub-apps pick
 * it up via the shared `<CanonicalUrl />` component.
 */
const PUBLIC_HOST = 'https://www.restworld.it';

/**
 * Renders `<link rel="canonical">` pointing at the public marketing URL
 * for the current path. Mount once in each sub-app's `_app.tsx` so every
 * page gets a canonical by default.
 *
 * Per-page overrides: render your own
 * `<Head><link rel="canonical" key="canonical" href="..." /></Head>` in
 * the page component. The `key="canonical"` deduplication makes Next.js
 * keep only the last tag with that key, so the page-level override wins.
 *
 * Note on the `/en/` locale: contrary to what this comment used to claim,
 * `router.asPath` does NOT include the locale prefix when i18n is enabled.
 * Next.js strips it and exposes it as `router.locale`
 * (`next/dist/client/index.js`, `normalizeLocalePath` on the initial
 * `asPath`), so on `/en/x` this component emits the Italian URL. The
 * per-locale canonical is built by the page layout, whose tag carries the
 * same `key` and therefore replaces this one; in worker-app every public
 * page goes through `LayoutWrapper`. Fixing it here as well would change
 * employer-app too, where a second canonical is emitted without a key.
 */
export function CanonicalUrl() {
  const router = useRouter();

  const path = router.asPath.split('?')[0].split('#')[0];
  const normalized = path === '/' ? '' : path;
  const href = `${PUBLIC_HOST}${normalized}`;

  return (
    <Head>
      <link rel="canonical" href={href} key="canonical" />
    </Head>
  );
}
